import type { Series } from '@/components/Chart/types';
import { getQuery } from '@/components/filter/helpers';
import { BaseApiClient } from '@/services/baseApiClient';
import type { BackendQuery } from '@/types/BackendQuery';
import type { RequestOptions } from '@/utils/request';
import type { Commodity } from '@enmon/commons';
import {
  AnalysisType,
  type CarbonFootprintAnalysisView,
  type ConsumptionAnalysisKind,
  type CostAnalysisKind,
  type SerializedAnalysisApiResponse,
  type SerializedDataIntegrityAnalysisApiResponse,
} from '@enmon/commons/lib/enmon';

// TODO: (https://enmontech.atlassian.net/browse/ENMON-2616):
// Analyze the use of this type in the application and try to get rid of it by replacing it with the types
// in the src\services\api\analysis\types\responses.ts
/**
 * @deprecated If this type is used inside of analysis service API's methods or hooks
 *             then use types from src\services\api\analysis\types\responses.ts
 *
 *             WARN: THIS IS A TEMPORARY TYPE
 */
export type AnalysisDataResponse = SerializedAnalysisApiResponse;

export type AnalysisFilter = Partial<BackendQuery>;

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * @deprecated Use response types from src\services\api\analysis\types\responses.ts instead
 */
export type GetAnalysisConsumptionResult = AnalysisDataResponse | null;
/**
 * @deprecated Use response types from src\services\api\analysis\types\responses.ts instead
 */
export type GetAnalysisCostResult = AnalysisDataResponse | null;
/**
 * @deprecated Use response types from src\services\api\analysis\types\responses.ts instead
 */
export type GetAnalysisValuesResult = AnalysisDataResponse | null;
/**
 * @deprecated Use response types from src\services\api\analysis\types\responses.ts instead
 */
export type GetAnalysisCarbonFootprintResult = AnalysisDataResponse | null;
/**
 * @deprecated Use response types from src\services\api\analysis\types\responses.ts instead
 */
export type GetAnalysisInvoicesResult = AnalysisDataResponse | null;
/**
 * @deprecated Use response types from src\services\api\analysis\types\responses.ts instead
 */
export type GetAnalysisExternalDataResult = AnalysisDataResponse | null;
/**
 * @deprecated Use response types from src\services\api\analysis\types\responses.ts instead
 */
export type GetAnalysisChartCategoriesResult = AnalysisDataResponse['categories'] | null;
export interface AnalysisQueryShape {
  queryFn: (signal?: AbortSignal) => Promise<Series[]>;
}
export class AnalysisApiClient extends BaseApiClient {
  constructor() {
    super('api/analysis');
  }

  public consumption(
    kind: ConsumptionAnalysisKind,
    filter: AnalysisFilter,
    signal: RequestOptions['signal'],
  ): Promise<GetAnalysisConsumptionResult> {
    return this.http.get<GetAnalysisConsumptionResult>(`${AnalysisType.Consumption}/${kind}`, {
      query: getQuery({ ...filter, timezone }),
      signal,
    });
  }

  public cost(
    kind: CostAnalysisKind,
    filter: AnalysisFilter,
    signal: RequestOptions['signal'],
  ): Promise<GetAnalysisCostResult> {
    return this.http.get<GetAnalysisCostResult>(`${AnalysisType.Cost}/${kind}`, {
      query: getQuery({ ...filter, timezone }),
      signal,
    });
  }

  public values(filter: AnalysisFilter, signal: RequestOptions['signal']): Promise<GetAnalysisValuesResult> {
    return this.http.get<GetAnalysisValuesResult>(AnalysisType.Values, {
      query: getQuery({ ...filter, timezone }),
      signal,
    });
  }

  public carbonFootprint(
    filter: AnalysisFilter,
    view: CarbonFootprintAnalysisView,
    signal: RequestOptions['signal'],
  ): Promise<GetAnalysisCarbonFootprintResult> {
    return this.http.get<GetAnalysisCarbonFootprintResult>(AnalysisType.CarbonFootprint, {
      query: getQuery({ ...filter, view, timezone }),
      signal,
    });
  }

  public invoices(
    commodity: Commodity | undefined,
    filter: AnalysisFilter,
    signal: RequestOptions['signal'],
  ): Promise<GetAnalysisInvoicesResult> {
    return this.http.get<GetAnalysisInvoicesResult>(`${AnalysisType.Invoices}/${commodity ?? ''}`, {
      query: getQuery({ ...filter, timezone }),
      signal,
    });
  }

  public chartCategories(
    filter: AnalysisFilter,
    signal: RequestOptions['signal'],
  ): Promise<GetAnalysisChartCategoriesResult> {
    return this.http.get<GetAnalysisChartCategoriesResult>(AnalysisType.ChartCategories, {
      query: getQuery({ ...filter, timezone }),
      signal,
    });
  }

  public dataIntegrity(
    filter: AnalysisFilter,
    signal: RequestOptions['signal'],
  ): Promise<SerializedDataIntegrityAnalysisApiResponse | null> {
    return this.http.get<SerializedDataIntegrityAnalysisApiResponse | null>(AnalysisType.DataIntegrity, {
      query: getQuery({ ...filter, timezone }),
      signal,
    });
  }
}
